// Currency derivation + formatting helpers.
//
// We map region (or fall back to symbol suffix) to an ISO currency + symbol.
// This avoids an extra yfinance .info call on every quote.

const SUFFIX_TO_CURRENCY = {
  ".NS": { code: "INR", sym: "₹" },
  ".BO": { code: "INR", sym: "₹" },
  ".L":  { code: "GBP", sym: "£" },
  ".PA": { code: "EUR", sym: "€" },
  ".AS": { code: "EUR", sym: "€" },
  ".DE": { code: "EUR", sym: "€" },
  ".MI": { code: "EUR", sym: "€" },
  ".MC": { code: "EUR", sym: "€" },
  ".SW": { code: "CHF", sym: "CHF " },
  ".T":  { code: "JPY", sym: "¥" },
  ".HK": { code: "HKD", sym: "HK$" },
  ".KS": { code: "KRW", sym: "₩" },
  ".SS": { code: "CNY", sym: "¥" },
  ".SZ": { code: "CNY", sym: "¥" },
  ".AX": { code: "AUD", sym: "A$" },
  ".TO": { code: "CAD", sym: "C$" },
  ".SI": { code: "SGD", sym: "S$" },
  ".TW": { code: "TWD", sym: "NT$" },
};

const INDEX_TO_CURRENCY = {
  "^GSPC": "USD", "^IXIC": "USD", "^DJI": "USD", "^RUT": "USD", "^VIX": "USD",
  "^NSEI": "INR", "^BSESN": "INR",
  "^FTSE": "GBP", "^GDAXI": "EUR", "^FCHI": "EUR", "^STOXX50E": "EUR",
  "^N225": "JPY", "^HSI": "HKD", "^KS11": "KRW", "^TWII": "TWD", "^AXJO": "AUD", "^STI": "SGD",
};

const REGION_TO_CURRENCY = {
  USA: { code: "USD", sym: "$" },
  India: { code: "INR", sym: "₹" },
  Europe: { code: "EUR", sym: "€" },
  Asia: { code: "JPY", sym: "¥" }, // fallback only; usually overridden by suffix
  Global: { code: "USD", sym: "$" },
};

const CODE_TO_SYM = {
  USD: "$", INR: "₹", EUR: "€", GBP: "£", JPY: "¥", CHF: "CHF ",
  HKD: "HK$", KRW: "₩", CNY: "¥", AUD: "A$", CAD: "C$", SGD: "S$", TWD: "NT$",
};

export function currencyFor({ symbol, region, assetClass }) {
  if (!symbol) return REGION_TO_CURRENCY[region] || REGION_TO_CURRENCY.Global;
  const s = String(symbol).toUpperCase();

  // Crypto: always USD (we use -USD pairs)
  if (assetClass === "crypto" || s.endsWith("-USD")) {
    return { code: "USD", sym: "$" };
  }
  // Forex pairs (EURUSD=X) — denominated in quote currency (last 3 letters)
  if (s.endsWith("=X")) {
    const quote = s.replace("=X", "").slice(-3);
    return { code: quote, sym: CODE_TO_SYM[quote] || quote + " " };
  }
  // Commodities futures =F → USD usually
  if (s.endsWith("=F")) return { code: "USD", sym: "$" };
  // Indices
  if (s.startsWith("^") && INDEX_TO_CURRENCY[s]) {
    const code = INDEX_TO_CURRENCY[s];
    return { code, sym: CODE_TO_SYM[code] || code + " " };
  }
  // Suffix-based
  for (const sfx of Object.keys(SUFFIX_TO_CURRENCY)) {
    if (s.endsWith(sfx)) return SUFFIX_TO_CURRENCY[sfx];
  }
  // Default by region
  return REGION_TO_CURRENCY[region] || REGION_TO_CURRENCY.Global;
}

// Format a price with the right currency symbol + locale-appropriate digits.
// `compact` collapses big numbers (e.g., $1.23K, ₹4.56L) — only used in tight UI.
export function formatPrice(price, opts = {}) {
  if (price == null || isNaN(price)) return "—";
  const { symbol, region, assetClass, compact = false } = opts;
  const { code, sym } = currencyFor({ symbol, region, assetClass });

  // Crypto can have many decimals for sub-dollar coins
  let digits = 2;
  if (assetClass === "crypto" && price < 1) digits = 4;
  if (assetClass === "crypto" && price < 0.01) digits = 6;
  // JPY/KRW typically zero decimals
  if (code === "JPY" || code === "KRW" || code === "TWD") digits = 0;

  if (compact && price >= 1000) {
    if (price >= 1e7) return `${sym}${(price / 1e7).toFixed(2)} Cr`;
    if (price >= 1e5 && (code === "INR")) return `${sym}${(price / 1e5).toFixed(2)} L`;
    if (price >= 1e6) return `${sym}${(price / 1e6).toFixed(2)}M`;
    if (price >= 1e3) return `${sym}${(price / 1e3).toFixed(1)}K`;
  }
  try {
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(price);
    return `${sym}${formatted}`;
  } catch {
    return `${sym}${price.toFixed(digits)}`;
  }
}

export function currencyCode(opts = {}) {
  return currencyFor(opts).code;
}
